import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as accountingSlice from '../../../data/accounting.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as triplegSlice from '../../../data/tripleg.slice';
import * as uiSlice from '../../../data/ui.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import {
  DataGridPro,
  type GridCallbackDetails,
  type GridPaginationModel,
  type GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import type { SortModel } from '../../../data/models/sort-model.model';
import TextSearch from '../../controls/TextSearch';
import {
  type Provider,
  TransportationProviderTier,
} from '../../../data/models/provider.model';

const PREFIX = 'SelectProviderDialog';

const classes = {
  content: `${PREFIX}-content`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface SelectProviderDialogProps {
  providerID?: number;
  open: boolean;
  close: (provider?: Provider) => void;
}

export default function SelectProviderDialog({
  providerID,
  open,
  close,
}: SelectProviderDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const loadingProviders = useAppSelector(
    providerSlice.selectLoadingProviderList,
  );
  const providers = useAppSelector(providerSlice.selectProviderList);
  const savedTripLeg = useAppSelector(triplegSlice.selectSavedTripLeg);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(5);
  const selectedFilter = 'active0'; // only get active providers
  const [searchFilter, setSearchFilter] = React.useState('');
  const sortModel: SortModel = {
    column: 'transportationProviderName',
    direction: 'asc',
  };
  const [selectedProvider, setSelectedProvider] = React.useState<Provider>();

  React.useEffect(() => {
    dispatch(
      providerSlice.fetchProviderListAsync({
        pageSize,
        page,
        filter: selectedFilter,
        search: searchFilter,
        sortModel,
        tiers: TransportationProviderTier.Tier1, // TODO: this should be '1,2' but the API doesn't allow it yet
      }),
    );
    dispatch(accountingSlice.fetchTransportationServicesAsync());
  }, [searchFilter, page, pageSize]);

  React.useEffect(() => {
    if (saveSuccessMessage && savedTripLeg) {
      handleCancel();
      dispatch(triplegSlice.setSavedTripLeg(false));
    }
  }, [saveSuccessMessage, savedTripLeg]);

  const handleCancel = () => {
    close();
  };

  const handleSave = () => {
    close(selectedProvider);
  };

  const onSearchChange = (val: string) => {
    setSearchFilter(val);
  };

  const onPageChange = (pg: number) => {
    setPage(pg);
  };

  const onPageSizeChange = (pgSize: number) => {
    setPageSize(pgSize);
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== providers.currentPage - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  const selectRow = (row: GridRowSelectionModel) => {
    const id = row[0];
    const provider = providers.items?.find(
      (p) => p.transportationProviderID === id,
    );
    setSelectedProvider(provider);
  };

  const columns = [
    {
      field: 'transportationProviderName',
      headerName: t('users.providerName') || '',
      flex: 2,
    },
  ];

  return (
    <RootDialog open={open} onClose={handleCancel} fullWidth maxWidth={'md'}>
      <DialogTitle>{t('users.selectProvider')}</DialogTitle>
      <DialogContent className={classes.content}>
        <Box className={classes.content}>
          <TextSearch
            id="text-search"
            icon={<SearchIcon />}
            label={t('triplegInventory.search')}
            placeholder={t('triplegInventory.searchPlaceholder')}
            currentSearchFilter={searchFilter}
            onSearchChange={onSearchChange}></TextSearch>

          <DataGridPro
            getRowId={(row: any) => row.transportationProviderID}
            columns={columns}
            rows={providers.items}
            rowCount={providers.itemCount}
            paginationModel={{ page: providers.currentPage - 1, pageSize }}
            autoHeight={true}
            paginationMode="server"
            sortingMode="server"
            pagination={true}
            pageSizeOptions={[5]}
            onPaginationModelChange={paginationChange}
            loading={loadingProviders}
            checkboxSelection={false}
            disableColumnMenu={true}
            disableColumnSelector={true}
            disableMultipleRowSelection={true}
            onRowSelectionModelChange={selectRow}
            rowSelectionModel={providerID ? [providerID] : []}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedProvider}
          onClick={handleSave}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
